import type {
  ContentObject,
  ContentTypeDefinition,
  PluginContentObject,
  WithOptionalInternalProps,
} from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback, useId, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { PluginSettingsChangedEvent } from '../../../lib/flotiq-plugins/plugin-events/PluginSettingsChangedEvent';
import { PluginsManageEvent } from '../../../lib/flotiq-plugins/plugin-events/PluginsManageEvent';
import { PluginsManageFormSchemaEvent } from '../../../lib/flotiq-plugins/plugin-events/PluginsManageFormSchemaEvent';
import { getTestProps } from '../../../lib/helpers';

import CancelButton from '../../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../../components/Button/predefined/SaveButton/SaveButton';
import ElementFromPlugin from '../../../components/ElementFromPlugin/ElementFromPlugin';

import usePluginResults from '../../../hooks/usePluginResults';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import ContentObjectForm from '../../../form/ContentObjectForm/ContentObjectForm';

export type UpdatePluginFn = (
  id: string,
  name: string,
  isNew: boolean,
  settings?: string,
) => Promise<[PluginContentObject | null, Record<string, string[]> | null]>;

interface ManageModalProps {
  /**
   * Plugin data
   */
  plugin: ContentObject;
  /**
   * Content types available
   */
  contentTypes: ContentTypeDefinition[];
  /**
   * Update plugin callback
   */
  updatePlugin: UpdatePluginFn;
  /**
   * Reload plugins setting callback
   */
  reloadUserPlugins: () => Promise<void>;
  /**
   * Component testid
   */
  testId?: string;
}

const ManageModal = ({
  plugin,
  contentTypes,
  updatePlugin,
  reloadUserPlugins,
  testId,
}: ManageModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const modalInstance = use(ModalInstanceContext);
  const formId = useId();

  const closeModal = useCallback(() => {
    modalInstance.resolve?.();
  }, [modalInstance]);

  const updateSettings = useCallback(
    async (
      newSettings: string,
    ): Promise<
      [string | undefined, Record<string, string | string[]> | null]
    > => {
      const [newPlugin, errors] = await updatePlugin(
        plugin.id,
        plugin.name,
        true,
        newSettings,
      );

      if (newPlugin) reloadUserPlugins();
      return [newPlugin?.settings, errors];
    },
    [plugin.id, plugin.name, reloadUserPlugins, updatePlugin],
  );

  const handleSubmit = useCallback(
    async (
      values: WithOptionalInternalProps<ContentObject>,
      userPlugin: ContentObject,
      pluginOnSubmit: any,
    ) => {
      setIsSaving(true);

      if (pluginOnSubmit) {
        const result = await pluginOnSubmit(values);

        setIsSaving(false);

        if (result?.[0]?.settings) {
          FlotiqPlugins.setPluginSettings(userPlugin.id, result[0].settings);
          FlotiqPlugins.runScoped(
            'flotiq.plugin.settings::changed',
            userPlugin.id,
            new PluginSettingsChangedEvent({ settings: result[0].settings }),
          );

          return [JSON.parse(result[0].settings), result[1]];
        }

        return result;
      } else {
        const [settings, errors] = await updateSettings(JSON.stringify(values));
        setIsSaving(false);

        if (settings) {
          closeModal();
          return [JSON.parse(settings), errors];
        }
        return [settings, errors];
      }
    },
    [closeModal, updateSettings],
  );

  /**
   * @emits FlotiqPlugins."flotiq.plugins.manage::render"
   */
  const pluginRenders = usePluginResults(
    'flotiq.plugins.manage::render',
    PluginsManageEvent,
    {
      plugin: plugin,
      reload: reloadUserPlugins,
      modalInstance,
      contentTypes,
      updateSettings,
    },
    plugin.id,
  );

  /**
   * @emits FlotiqPlugins."flotiq.plugins.manage::form-schema"
   */
  const schemaData = usePluginResults(
    'flotiq.plugins.manage::form-schema',
    PluginsManageFormSchemaEvent,
    {
      plugin: plugin,
      contentTypes,
      modalInstance,
      reload: reloadUserPlugins,
    },
    plugin.id,
  );

  if (schemaData?.length)
    return (
      <>
        {schemaData.map(({ schema, options }) => {
          return (
            <ContentObjectForm
              key={schema.id}
              contentObject={JSON.parse(plugin.settings || '{}')}
              contentType={{ ...schema, nonCtdSchema: true }}
              onSubmit={(values) =>
                handleSubmit(values, plugin, options?.onSubmit)
              }
              onValidate={options?.onValidate}
              disabledBuildInValidation={options?.disabledBuildInValidation}
              formId={formId}
              disabled={isSaving}
              additionalFormClasses="pb-0"
              isEditing
              {...getTestProps(testId, `${schema.id}-form`, 'testId')}
            />
          );
        })}
        <div
          className={twMerge(
            'w-full absolute left-0 bottom-0 flex items-center justify-between p-3 md:px-10 space-x-5',
            'border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900',
          )}
        >
          <CancelButton
            onClick={closeModal}
            disabled={isSaving}
            {...getTestProps(testId, 'cancel', 'testId')}
          />
          <SaveButton
            form={formId}
            isSaving={isSaving}
            {...getTestProps(testId, 'save', 'testId')}
          />
        </div>
      </>
    );

  return pluginRenders?.length ? (
    <ElementFromPlugin results={pluginRenders} />
  ) : null;
};

export default ManageModal;
